import React, { useState } from 'react';
import { format } from 'date-fns';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertTriangle, XCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';

interface PaymentReversalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payment: {
    id: string;
    payment_amount: number;
    payment_date: string;
    payment_method: string;
  };
  invoice: {
    id: string;
    invoice_number: string;
    total_amount: number;
    paid_amount: number;
    customer_id: string;
    estimate_id: string;
  };
}

export const PaymentReversalModal: React.FC<PaymentReversalModalProps> = ({
  open,
  onOpenChange,
  payment,
  invoice
}) => {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  // Check for related credits from overpayment
  const { data: relatedCredits } = useQuery({
    queryKey: ['related-credits', invoice.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customer_credits')
        .select('*')
        .eq('source_invoice_id', invoice.id)
        .eq('source_type', 'overpayment')
        .eq('status', 'issued');

      if (error) throw error;
      return data || [];
    },
    enabled: open
  });

  const reversalMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      if (!reason.trim()) {
        throw new Error('Reason is required');
      }

      // Mark payment as cancelled
      const { error: paymentError } = await supabase
        .from('invoice_payments')
        .update({
          is_cancelled: true,
          cancelled_at: new Date().toISOString(),
          cancelled_reason: reason.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', payment.id);

      if (paymentError) throw paymentError;

      // Get all active payments to recalculate
      const { data: activePayments, error: paymentsError } = await supabase
        .from('invoice_payments')
        .select('payment_amount')
        .eq('invoice_id', invoice.id)
        .or('is_cancelled.is.null,is_cancelled.eq.false');

      if (paymentsError) throw paymentsError;

      // Calculate new paid amount
      const newPaidAmount = (activePayments || []).reduce(
        (sum, p) => sum + (p.payment_amount || 0), 0
      );

      // Determine new status
      let newStatus = 'sent';
      if (newPaidAmount > 0 && newPaidAmount < invoice.total_amount) {
        newStatus = 'partially_paid';
      } else if (newPaidAmount >= invoice.total_amount) {
        newStatus = 'paid';
      }

      // Update invoice
      const { error: updateError } = await supabase
        .from('invoices')
        .update({
          paid_amount: newPaidAmount,
          status: newStatus,
          paid_at: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', invoice.id);

      if (updateError) throw updateError;

      // Cancel any related credits from this invoice and reset remaining amount to zero
      if (relatedCredits && relatedCredits.length > 0) {
        const { error: creditsError } = await supabase
          .from('customer_credits')
          .update({
            status: 'cancelled',
            remaining_amount: 0,
            cancelled_at: new Date().toISOString(),
            cancelled_by: user.id,
            cancelled_reason: `Payment reversed: ${reason.trim()}`,
            updated_at: new Date().toISOString()
          })
          .eq('source_invoice_id', invoice.id)
          .eq('source_type', 'overpayment')
          .eq('status', 'issued');

        if (creditsError) throw creditsError;
      }
    },
    onSuccess: () => {
      toast.success('Payment reversed successfully');
      queryClient.invalidateQueries({ queryKey: ['invoice', invoice.id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-payments', invoice.id] });
      queryClient.invalidateQueries({ queryKey: ['customer-credits'] });
      queryClient.invalidateQueries({ queryKey: ['estimateInvoices', invoice.estimate_id] });
      onOpenChange(false);
      setReason('');
    },
    onError: (error: Error) => {
      console.error('Error reversing payment:', error);
      toast.error(error.message || 'Failed to reverse payment');
    }
  });

  React.useEffect(() => {
    if (open) {
      setReason('');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-destructive" />
            Reverse Payment
          </DialogTitle>
          <DialogDescription>
            Reverse the payment of {formatCurrency(payment.payment_amount || 0)} made on{' '}
            {format(new Date(payment.payment_date), 'dd MMMM yyyy')}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Payment Details */}
          <div className="bg-muted p-3 rounded-md space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Payment Amount:</span>
              <span className="font-medium whitespace-nowrap font-mono">{formatCurrency(payment.payment_amount || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Payment Date:</span>
              <span>{format(new Date(payment.payment_date), 'dd MMMM yyyy')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Payment Method:</span>
              <span>{payment.payment_method || 'N/A'}</span>
            </div>
          </div>

          {/* Warning about related credits */}
          {relatedCredits && relatedCredits.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 rounded-md">
              <div className="flex gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800 dark:text-amber-200">
                    Credits will be cancelled
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    This invoice has {relatedCredits.length} credit(s) from overpayment that will be cancelled:
                  </p>
                  <ul className="list-disc list-inside text-sm mt-1 text-amber-700 dark:text-amber-300">
                    {relatedCredits.map((credit) => (
                      <li key={credit.id}>
                        {formatCurrency(credit.credit_amount || 0)} (Remaining: {formatCurrency(credit.remaining_amount || 0)})
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Consequences */}
          <div className="bg-destructive/10 border border-destructive/20 p-3 rounded-md text-sm">
            <p className="font-medium text-destructive">This action will:</p>
            <ul className="list-disc list-inside mt-1 space-y-1 text-destructive/80">
              <li>Mark this payment as cancelled</li>
              <li>Recalculate the invoice balance</li>
              <li>Update invoice status accordingly</li>
              {relatedCredits && relatedCredits.length > 0 && (
                <li>Cancel all related customer credits</li>
              )}
            </ul>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Reversal *</Label>
            <Textarea
              id="reason"
              placeholder="e.g., Payment returned, Bank error, Duplicate payment..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => reversalMutation.mutate()}
            disabled={reversalMutation.isPending || !reason.trim()}
            variant="destructive"
          >
            {reversalMutation.isPending ? 'Processing...' : 'Reverse Payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
